import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import api from '../services/api';
import useWorkspaceBasePath from '../hooks/useWorkspaceBasePath';
import { toast } from '../components/Toast';
import { Smartphone, Mail, MapPin, Wallet, Search, X, Trash2, Save, Send, Package, ScanBarcode, Camera, CameraOff, Volume2 } from 'lucide-react';
import logger from '../utils/logger';

const BusinessInvoiceForm = ({ projectMode = false }) => {
  // Contexte du projet si en mode projet
  const outletContext = useOutletContext();
  const projectId = projectMode ? outletContext?.projectId : null;
  
  // Base URL de l'API et routes selon le mode
  const apiBase = projectMode && projectId ? `/projects/${projectId}/workspace` : '/business';
  const routeBase = useWorkspaceBasePath();
  
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [showClientModal, setShowClientModal] = useState(false);

  // État pour l'autocomplétion client
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientDropdownRef = useRef(null);
  
  // État pour l'autocomplétion des produits
  const [activeProductDropdown, setActiveProductDropdown] = useState(null);
  const [productSearches, setProductSearches] = useState({});
  const dropdownRefs = useRef({});
  
  const [form, setForm] = useState({
    client_id: '',
    type: 'standard',
    date_facture: new Date().toISOString().split('T')[0],
    date_echeance: '',
    remise: 0,
    taxe: 0,
    notes: '',
    conditions: ''
  });
  
  const [items, setItems] = useState([
    { description: '', quantite: 1, prix_unitaire: '', product_id: '', batches: [] }
  ]);
  
  // Lots disponibles par produit (cache)
  const [productBatches, setProductBatches] = useState({});

  // État pour le scanner de codes-barres
  const [scanInput, setScanInput] = useState('');
  const [scanLoading, setScanLoading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [lastScannedProduct, setLastScannedProduct] = useState(null);
  const scanInputRef = useRef(null);
  const cameraRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const lastScanTimeRef = useRef(0);
  const lastScanCodeRef = useRef('');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, projectId]);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);
  
  // Fermer le dropdown quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeProductDropdown !== null) {
        const ref = dropdownRefs.current[activeProductDropdown];
        if (ref && !ref.contains(event.target)) {
          setActiveProductDropdown(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeProductDropdown]);

  // Fermer le dropdown client quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showClientDropdown && clientDropdownRef.current && !clientDropdownRef.current.contains(event.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showClientDropdown]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [clientsRes, productsRes] = await Promise.all([
        api.get(`${apiBase}/clients`),
        api.get(`${apiBase}/products`)
      ]);
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);

      if (id) {
        const res = await api.get(`${apiBase}/invoices/${id}`);
        if (res.data) {
          setForm({
            client_id: res.data.client_id || '',
            type: res.data.type || 'standard',
            date_facture: res.data.date_facture?.split(' ')[0] || new Date().toISOString().split('T')[0],
            date_echeance: res.data.date_echeance?.split(' ')[0] || '',
            remise: parseFloat(res.data.remise) || 0,
            taxe: parseFloat(res.data.taxe) || 0,
            notes: res.data.notes || '',
            conditions: res.data.conditions || ''
          });
          if (res.data.items && res.data.items.length > 0) {
            const loadedItems = res.data.items.map(item => ({
              id: item.id,
              description: item.description || '',
              quantite: item.quantite || 1,
              prix_unitaire: item.prix_unitaire || '',
              product_id: item.product_id || '',
              batches: []
            }));
            setItems(loadedItems);

            // Charger les lots pour les produits périssables existants
            const prods = Array.isArray(productsRes.data) ? productsRes.data : [];
            const perishableProductIds = [...new Set(
              loadedItems
                .filter(item => item.product_id)
                .map(item => String(item.product_id))
                .filter(pid => {
                  const prod = prods.find(p => String(p.id) === pid);
                  return prod && (prod.is_perishable === 1 || prod.is_perishable === true || prod.is_perishable === '1');
                })
            )];
            if (perishableProductIds.length > 0) {
              const batchResults = {};
              await Promise.all(perishableProductIds.map(async (pid) => {
                try {
                  const bRes = await api.get(`${apiBase}/products/${pid}/batches`);
                  batchResults[pid] = (Array.isArray(bRes.data) ? bRes.data : []).filter(b => b.actif && b.quantite > 0);
                } catch (e) {
                  logger.error('Erreur chargement lots produit', pid, e);
                  batchResults[pid] = [];
                }
              }));
              setProductBatches(prev => ({ ...prev, ...batchResults }));
            }
          }
        }
      }
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setItems([...items, { description: '', quantite: 1, prix_unitaire: '', product_id: '', batches: [] }]);
    setProductSearches(prev => ({ ...prev, [items.length]: '' }));
  };

  const handleRemoveItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
      const newSearches = { ...productSearches };
      delete newSearches[index];
      setProductSearches(newSearches);
    }
  };

  const getFilteredClients = () => {
    const term = clientSearch.toLowerCase().trim();
    if (!term) return clients.slice(0, 15);
    return clients.filter(c =>
      c.nom?.toLowerCase().includes(term) ||
      c.telephone?.toLowerCase().includes(term) ||
      c.email?.toLowerCase().includes(term)
    ).slice(0, 15);
  };

  const handleClientSelect = (client) => {
    setForm({ ...form, client_id: client.id });
    setClientSearch(client.nom);
    setShowClientDropdown(false);
  };

  const clearClient = () => {
    setForm({ ...form, client_id: '' });
    setClientSearch('');
  };

  // Sync le champ de recherche quand le client est pré-rempli (mode édition)
  useEffect(() => {
    if (form.client_id && clients.length > 0 && !clientSearch) {
      const c = clients.find(cl => String(cl.id) === String(form.client_id));
      if (c) setClientSearch(c.nom);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.client_id, clients]);

  const getProductStock = (productId) => {
    if (!productId) return null;
    const product = products.find(p => String(p.id) === String(productId));
    return product ? parseInt(product.stock_actuel) : null;
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;

    // Vérification stock en temps réel quand on change la quantité
    if (field === 'quantite' && newItems[index].product_id) {
      const stock = getProductStock(newItems[index].product_id);
      if (stock !== null && parseInt(value) > stock) {
        toast.warning(`Stock insuffisant pour "${newItems[index].description}": ${stock} disponible(s)`);
      }
    }

    setItems(newItems);
  };
  
  const handleProductSelect = async (index, product) => {
    const newItems = [...items];
    newItems[index].product_id = product.id;
    newItems[index].description = product.nom;
    newItems[index].prix_unitaire = product.prix_vente;
    newItems[index].batches = [];
    setItems(newItems);
    setProductSearches(prev => ({ ...prev, [index]: product.nom }));
    setActiveProductDropdown(null);
    
    // Charger les lots si produit périssable
    if (product.is_perishable === 1 || product.is_perishable === true) {
      await loadBatchesForProduct(product.id);
    }
  };
  
  const loadBatchesForProduct = async (productId) => {
    if (productBatches[productId]) return; // déjà chargé
    try {
      const res = await api.get(`${apiBase}/products/${productId}/batches`);
      const activeBatches = (Array.isArray(res.data) ? res.data : []).filter(b => b.actif && b.quantite > 0);
      setProductBatches(prev => ({ ...prev, [productId]: activeBatches }));
    } catch (err) {
      logger.error('Erreur chargement lots:', err);
    }
  };

  // =====================
  // SCANNER CODE-BARRES
  // =====================

  const SCAN_COOLDOWN = 2000; // 2 secondes entre chaque scan du même code

  const handleScanSubmit = async (code) => {
    const trimmedCode = (code || scanInput).trim();
    if (!trimmedCode || scanLoading) return;

    // Anti-rafale : ignorer si même code scanné dans les 2 dernières secondes
    const now = Date.now();
    if (trimmedCode === lastScanCodeRef.current && now - lastScanTimeRef.current < SCAN_COOLDOWN) {
      return;
    }
    lastScanCodeRef.current = trimmedCode;
    lastScanTimeRef.current = now;

    setScanLoading(true);
    setLastScannedProduct(null);
    try {
      const res = await api.get(`${apiBase}/products/scan?code=${encodeURIComponent(trimmedCode)}`);
      if (res.data.success && res.data.product) {
        const product = res.data.product;
        addScannedProduct(product);
        setLastScannedProduct(product);
        // Bip sonore de succès
        playBeep(true);
      } else {
        toast.error(`Aucun produit trouvé pour le code "${trimmedCode}"`);
        playBeep(false);
      }
    } catch (err) {
      logger.error(err);
      toast.error('Erreur lors de la recherche du produit');
      playBeep(false);
    } finally {
      setScanLoading(false);
      setScanInput('');
      scanInputRef.current?.focus();
    }
  };

  const addScannedProduct = (product) => {
    setItems(prev => {
      // Vérifier si le produit est déjà dans la liste
      const existingIdx = prev.findIndex(item => String(item.product_id) === String(product.id));
      if (existingIdx >= 0) {
        // Incrémenter la quantité
        const newQty = parseInt(prev[existingIdx].quantite) + 1;
        const stock = parseInt(product.stock_actuel);
        if (!isNaN(stock) && newQty > stock) {
          toast.warning(`Stock insuffisant pour "${product.nom}" : ${stock} disponible(s)`);
          return prev;
        }
        const updated = [...prev];
        updated[existingIdx] = { 
          ...updated[existingIdx], 
          quantite: newQty 
        };
        return updated;
      }
      // Ajouter comme nouveau produit
      const newItem = {
        description: product.nom,
        quantite: 1,
        prix_unitaire: product.prix_vente,
        product_id: product.id,
        batches: []
      };
      // Si la première ligne est vide, la remplacer
      if (prev.length === 1 && !prev[0].description && !prev[0].product_id) {
        return [newItem];
      }
      return [...prev, newItem];
    });

    // Charger les lots si périssable
    if (product.is_perishable === 1 || product.is_perishable === true || product.is_perishable === '1') {
      loadBatchesForProduct(product.id);
    }
  };

  const playBeep = (success) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = success ? 800 : 300;
      gain.gain.value = 0.3;
      osc.start();
      osc.stop(ctx.currentTime + (success ? 0.15 : 0.3));
    } catch (e) { /* ignore audio errors */ }
  };

  const startCamera = async () => {
    setShowCamera(true);
    // Attendre que le DOM soit rendu
    setTimeout(async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        const scanner = new Html5Qrcode('barcode-camera-reader');
        html5QrCodeRef.current = scanner;
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 150 } },
          (decodedText) => {
            // Code détecté
            handleScanSubmit(decodedText);
          },
          () => { /* ignore scan errors */ }
        );
      } catch (err) {
        logger.error('Erreur caméra:', err);
        toast.error('Impossible d\'accéder à la caméra');
        setShowCamera(false);
      }
    }, 100);
  };

  const stopCamera = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
      } catch (e) { /* ignore */ }
      html5QrCodeRef.current = null;
    }
    setShowCamera(false);
  };
  
  const handleBatchQuantityChange = (itemIndex, batchId, qty) => {
    const newItems = [...items];
    const item = newItems[itemIndex];
    const batch = productBatches[item.product_id]?.find(b => b.id === batchId);
    const maxQty = batch ? batch.quantite : 0;
    const val = Math.max(0, Math.min(parseInt(qty) || 0, maxQty));
    
    // Mettre à jour ou ajouter l'allocation
    const existingIdx = (item.batches || []).findIndex(ba => ba.batch_id === batchId);
    if (!item.batches) item.batches = [];
    if (existingIdx >= 0) {
      if (val === 0) {
        item.batches.splice(existingIdx, 1);
      } else {
        item.batches[existingIdx].quantite = val;
      }
    } else if (val > 0) {
      item.batches.push({ batch_id: batchId, quantite: val });
    }
    
    // Mettre à jour la quantité totale de l'item
    const totalFromBatches = item.batches.reduce((sum, ba) => sum + ba.quantite, 0);
    item.quantite = totalFromBatches || 1;
    
    setItems(newItems);
  };
  
  const handleProductSearchChange = (index, value) => {
    setProductSearches(prev => ({ ...prev, [index]: value }));
    setActiveProductDropdown(index);
    
    // Si on efface, réinitialiser le produit sélectionné
    if (value === '') {
      const newItems = [...items];
      newItems[index].product_id = '';
      newItems[index].batches = [];
      setItems(newItems);
    }
  };
  
  const getFilteredProducts = (index) => {
    const search = (productSearches[index] || '').toLowerCase();
    if (!search) return products.slice(0, 10);
    return products.filter(p => 
      p.nom?.toLowerCase().includes(search) ||
      p.reference?.toLowerCase().includes(search) ||
      p.categorie?.toLowerCase().includes(search)
    ).slice(0, 10);
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => {
      return sum + (parseFloat(item.quantite) || 0) * (parseFloat(item.prix_unitaire) || 0);
    }, 0);
  };

  const handleSubmit = async (e, action = 'save') => {
    e.preventDefault();
    
    // Validation
    if (!form.client_id) {
      toast.warning('Veuillez sélectionner un client');
      return;
    }
    
    const validItems = items.filter(item => item.description && item.prix_unitaire);
    if (validItems.length === 0) {
      toast.warning('Ajoutez au moins une ligne de facture');
      return;
    }

    // Vérification du stock pour chaque produit
    const stockErrors = [];
    validItems.forEach(item => {
      if (item.product_id) {
        const stock = getProductStock(item.product_id);
        if (stock !== null && parseInt(item.quantite) > stock) {
          stockErrors.push(`"${item.description}" : ${item.quantite} demandé(s), ${stock} en stock`);
        }
      }
    });
    if (stockErrors.length > 0) {
      toast.error(`Stock insuffisant :\n${stockErrors.join('\n')}`);
      return;
    }

    // Calculer le total de chaque item et les totaux globaux
    const itemsWithTotal = validItems.map(item => ({
      ...item,
      quantite: parseFloat(item.quantite) || 1,
      prix_unitaire: parseFloat(item.prix_unitaire) || 0,
      total: (parseFloat(item.quantite) || 1) * (parseFloat(item.prix_unitaire) || 0)
    }));
    
    const sousTotal = itemsWithTotal.reduce((sum, item) => sum + item.total, 0);
    const remise = parseFloat(form.remise) || 0;
    const taxe = parseFloat(form.taxe) || 0;
    const total = sousTotal - remise + taxe;

    setSaving(true);
    try {
      const data = {
        ...form,
        items: itemsWithTotal,
        sous_total: sousTotal,
        remise: remise,
        taxe: taxe,
        total: total,
        statut: action === 'send' ? 'envoyee' : 'brouillon'
      };

      if (id) {
        await api.put(`${apiBase}/invoices/${id}`, data);
      } else {
        await api.post(`${apiBase}/invoices`, data);
      }
      
      navigate(`${routeBase}/invoices`);
    } catch (err) {
      logger.error(err);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const subtotal = calculateSubtotal();
  const remiseVal = parseFloat(form.remise) || 0;
  const taxeVal = parseFloat(form.taxe) || 0;
  const totalFinal = subtotal - remiseVal + taxeVal;
  const selectedClient = clients.find(c => String(c.id) === String(form.client_id));

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">

        <h1 className="text-2xl font-bold text-gray-800">
          {id ? 'Modifier la facture' : 'Nouvelle facture'}
        </h1>
      </div>

      <form onSubmit={(e) => handleSubmit(e, 'save')}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client Selection */}
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="font-semibold text-gray-800 mb-4">Client</h2>
              
              <div className="flex gap-3">
                <div className="flex-1 relative" ref={clientDropdownRef}>
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-3 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher un client (nom, tél, email)..."
                      value={clientSearch}
                      onChange={(e) => {
                        setClientSearch(e.target.value);
                        setShowClientDropdown(true);
                        if (form.client_id) {
                          const c = clients.find(cl => String(cl.id) === String(form.client_id));
                          if (c && e.target.value !== c.nom) {
                            setForm({ ...form, client_id: '' });
                          }
                        }
                      }}
                      onFocus={() => setShowClientDropdown(true)}
                      className={`w-full border rounded-xl pl-10 pr-10 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${form.client_id ? 'border-green-400 bg-green-50' : ''}`}
                    />
                    {form.client_id && (
                      <button
                        type="button"
                        onClick={clearClient}
                        className="absolute right-3 top-2.5 text-gray-400 hover:text-red-500 transition"
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>

                  {showClientDropdown && !form.client_id && (
                    <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-72 overflow-y-auto">
                      {getFilteredClients().length > 0 ? (
                        getFilteredClients().map(client => (
                          <button
                            key={client.id}
                            type="button"
                            onClick={() => handleClientSelect(client)}
                            className="w-full text-left px-4 py-3 hover:bg-indigo-50 transition border-b border-gray-50 last:border-0"
                          >
                            <div className="flex items-center justify-between">
                              <div className="font-medium text-gray-800">{client.nom}</div>
                              {client.credit_balance > 0 && (
                                <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                                  <Wallet size={12} className="inline mr-0.5" /> {parseFloat(client.credit_balance).toLocaleString()} XOF
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                              {client.telephone && <span><Smartphone size={12} className="inline mr-0.5" /> {client.telephone}</span>}
                              {client.email && <span><Mail size={12} className="inline mr-0.5" /> {client.email}</span>}
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center">
                          Aucun client trouvé
                          <button
                            type="button"
                            onClick={() => { setShowClientDropdown(false); setShowClientModal(true); }}
                            className="block mx-auto mt-1 text-indigo-600 font-medium hover:underline"
                          >
                            + Créer ce client
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setShowClientModal(true)}
                  className="px-4 py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition whitespace-nowrap"
                >
                  + Nouveau
                </button>
              </div>
              
              {selectedClient && (
                <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                  <div className="text-sm text-gray-600">
                    <div className="font-medium text-gray-800">{selectedClient.nom}</div>
                    {selectedClient.telephone && <div><Smartphone size={14} className="inline" /> {selectedClient.telephone}</div>}
                    {selectedClient.email && <div><Mail size={14} className="inline" /> {selectedClient.email}</div>}
                    {selectedClient.adresse && <div><MapPin size={14} className="inline" /> {selectedClient.adresse}</div>}
                    {selectedClient.credit_balance > 0 && (
                      <div className="mt-2 text-green-600 font-medium">
                        <Wallet size={14} className="inline" /> Crédit disponible: {parseFloat(selectedClient.credit_balance).toLocaleString()} XOF
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Invoice Details */}
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="font-semibold text-gray-800 mb-4">Détails facture</h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="standard">Facture standard</option>
                    <option value="proforma">Facture proforma</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date facture</label>
                  <input
                    type="date"
                    value={form.date_facture}
                    onChange={(e) => setForm({ ...form, date_facture: e.target.value })}
                    className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date d'échéance</label>
                  <input
                    type="date"
                    value={form.date_echeance}
                    onChange={(e) => setForm({ ...form, date_echeance: e.target.value })}
                    className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>

            {/* Invoice Items */}
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="font-semibold text-gray-800 mb-4">Articles / Services</h2>

              {/* Scanner code-barres */}
              <div className="mb-5 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <ScanBarcode size={20} className="text-indigo-600" />
                  <span className="font-medium text-indigo-800 text-sm">Scanner un produit</span>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input
                      ref={scanInputRef}
                      type="text"
                      value={scanInput}
                      onChange={(e) => setScanInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleScanSubmit(); } }}
                      placeholder="Scannez ou tapez le code-barres..."
                      className="w-full border border-indigo-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 pr-10"
                      autoComplete="off"
                    />
                    {scanLoading && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-600 border-t-transparent"></div>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleScanSubmit()}
                    disabled={!scanInput.trim() || scanLoading}
                    className="px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition text-sm font-medium"
                  >
                    <Search size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={showCamera ? stopCamera : startCamera}
                    className={`px-4 py-2.5 rounded-lg transition text-sm font-medium ${
                      showCamera
                        ? 'bg-red-500 text-white hover:bg-red-600'
                        : 'bg-white border border-indigo-200 text-indigo-600 hover:bg-indigo-50'
                    }`}
                    title={showCamera ? 'Arrêter la caméra' : 'Scanner avec la caméra'}
                  >
                    {showCamera ? <CameraOff size={16} /> : <Camera size={16} />}
                  </button>
                </div>

                {/* Zone caméra */}
                {showCamera && (
                  <div className="mt-3 rounded-lg overflow-hidden border border-indigo-200">
                    <div id="barcode-camera-reader" className="w-full" style={{ minHeight: '200px' }}></div>
                    <div className="bg-indigo-100 px-3 py-2 text-xs text-indigo-700 text-center">
                      Pointez la caméra vers un code-barres
                    </div>
                  </div>
                )}

                {/* Dernier produit scanné */}
                {lastScannedProduct && (
                  <div className="mt-3 flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-3 py-2 animate-pulse-once">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Package size={14} className="text-green-600" />
                    </div>
                    <div className="flex-1 text-sm">
                      <span className="font-medium text-green-800">{lastScannedProduct.nom}</span>
                      <span className="mx-2 text-green-400">•</span>
                      <span className="text-green-600">{parseInt(lastScannedProduct.prix_vente).toLocaleString()} FCFA</span>
                    </div>
                    <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full font-medium">Ajouté !</span>
                  </div>
                )}
              </div>
              
              <div className="space-y-4">
                {items.map((item, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-xl">
                    {/* Ligne 1: Produit et Description */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                      <div className="relative" ref={el => dropdownRefs.current[index] = el}>
                        <label className="block text-xs text-gray-500 mb-1">Produit (optionnel)</label>
                        <input
                          type="text"
                          value={productSearches[index] ?? (item.product_id ? products.find(p => String(p.id) === String(item.product_id))?.nom : '') ?? ''}
                          onChange={(e) => handleProductSearchChange(index, e.target.value)}
                          onFocus={() => setActiveProductDropdown(index)}
                          placeholder="Rechercher un produit..."
                          className="w-full border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500"
                        />
                        
                        {/* Dropdown autocomplétion */}
                        {activeProductDropdown === index && (
                          <div className="absolute z-50 w-full mt-1 bg-white border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                            {getFilteredProducts(index).length === 0 ? (
                              <div className="px-4 py-3 text-gray-500 text-sm text-center">
                                Aucun produit trouvé
                              </div>
                            ) : (
                              getFilteredProducts(index).map(product => (
                                <button
                                  type="button"
                                  key={product.id}
                                  onClick={() => handleProductSelect(index, product)}
                                  className={`w-full px-4 py-2 text-left hover:bg-indigo-50 flex items-center gap-3 ${
                                    String(item.product_id) === String(product.id) ? 'bg-indigo-50' : ''
                                  }`}
                                >
                                  <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center text-white text-xs font-bold">
                                    {product.nom?.charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 truncate text-sm">{product.nom}</p>
                                    <p className="text-xs text-gray-500">
                                      {product.reference && <span className="mr-2">#{product.reference}</span>}
                                      {product.categorie && <span className="text-indigo-600">{product.categorie}</span>}
                                    </p>
                                  </div>
                                  <span className="text-sm font-semibold text-green-600 whitespace-nowrap">
                                    {parseFloat(product.prix_vente).toLocaleString()} XOF
                                  </span>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                        
                        {item.product_id && (
                          <button
                            type="button"
                            onClick={() => {
                              handleItemChange(index, 'product_id', '');
                              setProductSearches(prev => ({ ...prev, [index]: '' }));
                            }}
                            className="absolute right-2 top-7 text-gray-400 hover:text-red-500"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Description *</label>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          placeholder="Description de l'article ou service"
                          className="w-full border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500"
                          required
                        />
                      </div>
                    </div>
                    
                    {/* Ligne 2: Quantité, Prix unitaire, Bouton supprimer */}
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Quantité</label>
                        <input
                          type="number"
                          min="1"
                          max={item.product_id ? (getProductStock(item.product_id) ?? undefined) : undefined}
                          value={item.quantite}
                          onChange={(e) => handleItemChange(index, 'quantite', e.target.value)}
                          readOnly={!!(item.batches && item.batches.length > 0)}
                          className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 text-center ${item.batches && item.batches.length > 0 ? 'bg-orange-50 cursor-not-allowed' : ''} ${item.product_id && getProductStock(item.product_id) !== null && parseInt(item.quantite) > getProductStock(item.product_id) ? 'border-red-500 bg-red-50 ring-red-300' : ''}`}
                          required
                        />
                        {item.product_id && getProductStock(item.product_id) !== null && (
                          <div className={`text-xs mt-0.5 ${parseInt(item.quantite) > getProductStock(item.product_id) ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                            Stock: {getProductStock(item.product_id)}
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Prix unitaire (XOF)</label>
                        <input
                          type="number"
                          min="0"
                          value={item.prix_unitaire}
                          onChange={(e) => handleItemChange(index, 'prix_unitaire', e.target.value)}
                          placeholder="0"
                          className="w-full border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500"
                          required
                        />
                      </div>
                      <div className="flex items-end">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          disabled={items.length === 1}
                          className="w-full py-2.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          <Trash2 size={16} />
                          <span className="hidden md:inline text-sm">Supprimer</span>
                        </button>
                      </div>
                      <div className="hidden md:flex items-end justify-end">
                        <div className="text-right">
                          <span className="text-xs text-gray-500 block">Sous-total</span>
                          <span className="font-semibold text-gray-800">
                            {((parseFloat(item.quantite) || 0) * (parseFloat(item.prix_unitaire) || 0)).toLocaleString()} XOF
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Sous-total mobile */}
                    <div className="md:hidden text-right mt-3 pt-3 border-t border-gray-200">
                      <span className="text-sm font-medium text-gray-600">
                        Sous-total: <span className="text-gray-800">{((parseFloat(item.quantite) || 0) * (parseFloat(item.prix_unitaire) || 0)).toLocaleString()} XOF</span>
                      </span>
                    </div>

                    {/* Sélection des lots pour produits périssables */}
                    {(() => {
                      const prod = products.find(p => String(p.id) === String(item.product_id));
                      const batches = productBatches[item.product_id];
                      if (!prod || !(prod.is_perishable === 1 || prod.is_perishable === true)) return null;
                      
                      return (
                        <div className="mt-3 pt-3 border-t border-orange-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Package size={14} className="text-orange-500" />
                            <span className="text-xs font-semibold text-orange-700">Répartition par lot</span>
                          </div>
                          {!batches ? (
                            <p className="text-xs text-gray-400">Chargement des lots...</p>
                          ) : batches.length === 0 ? (
                            <p className="text-xs text-orange-500">Aucun lot disponible avec du stock</p>
                          ) : (
                            <div className="space-y-1.5">
                              {batches.map(batch => {
                                const alloc = (item.batches || []).find(ba => ba.batch_id === batch.id);
                                const allocQty = alloc ? alloc.quantite : 0;
                                const daysLeft = batch.date_expiration ? Math.ceil((new Date(batch.date_expiration) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                                return (
                                  <div key={batch.id} className={`flex items-center gap-3 p-2 rounded-lg text-sm ${daysLeft !== null && daysLeft < 0 ? 'bg-red-50' : daysLeft !== null && daysLeft <= 7 ? 'bg-yellow-50' : 'bg-orange-50'}`}>
                                    <div className="flex-1 min-w-0">
                                      <span className="font-medium text-gray-700">{batch.numero_lot || 'Sans n°'}</span>
                                      <span className="text-xs text-gray-500 ml-2">
                                        Dispo: {batch.quantite}
                                        {batch.date_expiration && (
                                          <> • Exp: {new Date(batch.date_expiration).toLocaleDateString('fr-FR')}
                                            {daysLeft !== null && (
                                              <span className={daysLeft < 0 ? 'text-red-600 font-medium' : daysLeft <= 7 ? 'text-yellow-600' : 'text-green-600'}>
                                                {' '}({daysLeft < 0 ? 'expiré' : `${daysLeft}j`})
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </span>
                                    </div>
                                    <input
                                      type="number"
                                      min="0"
                                      max={batch.quantite}
                                      value={allocQty}
                                      onChange={(e) => handleBatchQuantityChange(index, batch.id, e.target.value)}
                                      className="w-16 text-center border rounded-lg py-1 text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
                                      placeholder="0"
                                    />
                                  </div>
                                );
                              })}
                              {(item.batches || []).length > 0 && (
                                <div className="text-xs text-right text-orange-600 font-medium">
                                  Total lots: {(item.batches || []).reduce((s, ba) => s + ba.quantite, 0)} unité(s)
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
              
              <button
                type="button"
                onClick={handleAddItem}
                className="mt-4 px-4 py-2 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-indigo-500 hover:text-indigo-600 transition w-full"
              >
                + Ajouter une ligne
              </button>
            </div>

            {/* Notes */}
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="font-semibold text-gray-800 mb-4">Notes & Conditions</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes (visible sur la facture)</label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows="2"
                    className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                    placeholder="Notes ou remarques..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Conditions de paiement</label>
                  <textarea
                    value={form.conditions}
                    onChange={(e) => setForm({ ...form, conditions: e.target.value })}
                    rows="2"
                    className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                    placeholder="Ex: Paiement à 30 jours..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow p-6 sticky top-6">
              <h2 className="font-semibold text-gray-800 mb-4">Récapitulatif</h2>
              
              <div className="space-y-3">
                <div className="flex justify-between text-gray-600">
                  <span>Sous-total ({items.filter(i => i.description).length} article{items.filter(i => i.description).length > 1 ? 's' : ''})</span>
                  <span>{subtotal.toLocaleString()} XOF</span>
                </div>
                
                {/* Remise */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Remise (XOF)</label>
                  <input
                    type="number"
                    min="0"
                    value={form.remise}
                    onChange={(e) => setForm({ ...form, remise: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="0"
                  />
                </div>
                {remiseVal > 0 && (
                  <div className="flex justify-between text-red-500 text-sm">
                    <span>Remise</span>
                    <span>-{remiseVal.toLocaleString()} XOF</span>
                  </div>
                )}
                
                {/* Taxe */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Taxe (XOF)</label>
                  <input
                    type="number"
                    min="0"
                    value={form.taxe}
                    onChange={(e) => setForm({ ...form, taxe: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="0"
                  />
                </div>
                {taxeVal > 0 && (
                  <div className="flex justify-between text-gray-600 text-sm">
                    <span>Taxe</span>
                    <span>+{taxeVal.toLocaleString()} XOF</span>
                  </div>
                )}
                
                <div className="border-t pt-3">
                  <div className="flex justify-between text-lg font-bold text-gray-800">
                    <span>Total</span>
                    <span className="text-indigo-600">{totalFinal.toLocaleString()} XOF</span>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 space-y-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition disabled:opacity-50"
                >
                  {saving ? 'Enregistrement...' : <><Save size={16} className="inline" /> Enregistrer brouillon</>}
                </button>
                
                {id && (
                  <button
                    type="button"
                    onClick={(e) => handleSubmit(e, 'send')}
                    disabled={saving}
                    className="w-full py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition disabled:opacity-50"
                  >
                    {saving ? 'Envoi...' : <><Send size={16} className="inline" /> Enregistrer &amp; Envoyer</>}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Quick Add Client Modal */}
      {showClientModal && (
        <QuickClientModal 
          onClose={() => setShowClientModal(false)}
          onCreated={(newClient) => {
            setClients([...clients, newClient]);
            setForm({ ...form, client_id: newClient.id });
            setClientSearch(newClient.nom);
            setShowClientModal(false);
          }}
          apiBase={apiBase}
        />
      )}
    </div>
  );
};

// Quick Client Modal Component
const QuickClientModal = ({ onClose, onCreated, apiBase = '/business' }) => {
  const [form, setForm] = useState({ nom: '', telephone: '', email: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post(`${apiBase}/clients`, form);
      if (res.data && res.data.id) {
        onCreated({ id: res.data.id, ...form });
      }
    } catch (err) {
      logger.error(err);
      toast.error('Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">Nouveau client</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
            <input
              type="text"
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
            <input
              type="text"
              value={form.telephone}
              onChange={(e) => setForm({ ...form, telephone: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Création...' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusinessInvoiceForm;
